import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  ArrowLeft,
  Check,
  Loader2,
  Share2,
  Telescope,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { useMe, AuthError, goToLogin } from '@/lib/auth';
import { useAccounts } from '@/lib/accounts';
import {
  useDeleteResearch,
  useResearchList,
  useResearchReport,
  useShareResearch,
  useStartResearch,
  type ReportSection,
  type ResearchSummary,
} from '@/lib/research';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/research')({
  validateSearch: (s: Record<string, unknown>): { open?: number } => ({
    open: s.open != null && Number.isFinite(Number(s.open)) ? Number(s.open) : undefined,
  }),
  component: Research,
});

function Research() {
  const { data: me, isLoading, error } = useMe();
  const { open } = Route.useSearch();
  const [openId, setOpenId] = useState<number | null>(open ?? null);
  useEffect(() => {
    if (open != null) setOpenId(open);
  }, [open]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <Loader2 className="size-5 animate-spin" strokeWidth={1.75} />
      </div>
    );
  }
  if (error instanceof AuthError) {
    return (
      <div className="flex h-full items-center justify-center px-6">
        <div className="w-full max-w-sm rounded-2xl border bg-card p-8 text-center shadow-sm">
          <h1 className="text-xl font-semibold tracking-tight">Sign in to continue</h1>
          <Button onClick={goToLogin} className="mt-6 w-full">
            Sign in with Clougency
          </Button>
        </div>
      </div>
    );
  }
  if (!me) return null;

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto w-full max-w-4xl px-4 py-6 md:px-8 md:py-8">
        {openId != null ? (
          <ReportView id={openId} onBack={() => setOpenId(null)} />
        ) : (
          <ResearchHome onOpen={setOpenId} />
        )}
      </div>
    </div>
  );
}

function ResearchHome({ onOpen }: { onOpen: (id: number) => void }) {
  const { data: accountsData } = useAccounts();
  const accounts = accountsData?.accounts ?? [];
  const listQ = useResearchList();
  const start = useStartResearch();
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const [question, setQuestion] = useState('');

  // Default to the first inbox once accounts load.
  useEffect(() => {
    if (selected.size === 0 && accounts[0]) setSelected(new Set([accounts[0].id]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts.length]);

  function toggleAccount(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        if (next.size === 1) return prev; // at least one inbox stays selected
        next.delete(id);
      } else next.add(id);
      return next;
    });
  }

  function submit() {
    if (selected.size === 0 || question.trim().length < 8) return;
    start.mutate(
      { accountIds: [...selected], question: question.trim() },
      {
        onSuccess: () => {
          setQuestion('');
          toast.success("Research started — you'll get a notification when it's ready");
        },
        onError: (e) =>
          toast.error(
            e instanceof Error && e.message === 'rate_limited'
              ? 'Research limit reached — try again in a while'
              : 'Could not start the research',
          ),
      },
    );
  }

  const reports = listQ.data?.reports ?? [];

  return (
    <>
      <header className="mb-5">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Telescope className="size-6 text-violet-500" strokeWidth={1.75} />
          Research
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Deep investigations over your mail — every matching message is scanned, not sampled.
          Pick one inbox, or several when a story spans them. Reports are private to you until
          you share them.
        </p>
      </header>

      <div className="rounded-lg border p-4">
        <div className="mb-2 flex flex-wrap items-center gap-1.5 text-xs font-medium text-muted-foreground">
          Investigate
          {accounts.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => toggleAccount(a.id)}
              className={cn(
                'rounded-full border px-2.5 py-1 text-[11px] transition-colors',
                selected.has(a.id)
                  ? 'border-violet-500/50 bg-violet-500/10 text-violet-700 dark:text-violet-300'
                  : 'text-muted-foreground hover:bg-muted/50',
              )}
              aria-pressed={selected.has(a.id)}
            >
              {a.emailAddress}
            </button>
          ))}
          <span className="text-[10px] font-normal">
            {selected.size > 1 ? `${selected.size} inboxes` : 'one inbox'} — tap to add more
          </span>
        </div>
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          rows={2}
          placeholder='e.g. "List all the websites Sandra has ever mentioned" · "Every amount John quoted us this year" · "Timeline of our correspondence with Acme"'
          className="w-full resize-y rounded-md border bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
        />
        <div className="mt-2 flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">
            Runs in the background (a few minutes) — you'll be notified.
          </span>
          <Button
            size="sm"
            onClick={submit}
            disabled={start.isPending || selected.size === 0 || question.trim().length < 8}
          >
            {start.isPending && <Loader2 className="size-3.5 animate-spin" />}
            Start research
          </Button>
        </div>
      </div>

      <div className="mt-6 space-y-2">
        {listQ.isLoading && (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading reports…
          </div>
        )}
        {!listQ.isLoading && reports.length === 0 && (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No research yet — ask your first question above.
          </p>
        )}
        {reports.map((r) => (
          <ReportRow key={r.id} r={r} onOpen={() => onOpen(r.id)} />
        ))}
      </div>
    </>
  );
}

function StatusChip({ r }: { r: ResearchSummary }) {
  if (r.status === 'done')
    return (
      <span className="inline-flex items-center gap-1 rounded bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-700 dark:text-emerald-400">
        <Check className="size-3" strokeWidth={2.5} /> done
      </span>
    );
  if (r.status === 'failed')
    return (
      <span className="inline-flex items-center gap-1 rounded bg-destructive/10 px-1.5 py-0.5 text-[10px] text-destructive">
        <TriangleAlert className="size-3" strokeWidth={2} /> failed
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 rounded bg-violet-500/10 px-1.5 py-0.5 text-[10px] text-violet-700 dark:text-violet-300">
      <Loader2 className="size-3 animate-spin" strokeWidth={2} />
      {r.status === 'queued' ? 'queued' : r.progress || 'running…'}
    </span>
  );
}

function ReportRow({ r, onOpen }: { r: ResearchSummary; onOpen: () => void }) {
  const del = useDeleteResearch();
  return (
    <div
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-lg border p-3 hover:bg-muted/30',
        r.status !== 'done' && 'cursor-default',
      )}
      onClick={() => r.status === 'done' && onOpen()}
    >
      <Telescope className="mt-0.5 size-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{r.question}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          <StatusChip r={r} />
          <span>{r.accountIds.length > 1 ? `${r.accountIds.length} inboxes` : (r.accountEmail ?? 'inbox removed')}</span>
          <span>· {r.mine ? 'you' : r.author}</span>
          {r.shared && <span className="rounded bg-muted px-1.5 py-0.5 text-[10px]">shared</span>}
          {r.status === 'done' && r.stats?.matchingMessages != null && (
            <span>
              · {r.stats.matchingMessages} messages analysed
              {r.stats.tookMs ? ` in ${Math.round(r.stats.tookMs / 1000)}s` : ''}
            </span>
          )}
          {r.status === 'failed' && r.error && <span className="text-destructive">{r.error}</span>}
        </div>
      </div>
      {r.mine && (
        <Button
          variant="ghost"
          size="icon"
          className="size-7 shrink-0 text-muted-foreground hover:text-destructive"
          onClick={(e) => {
            e.stopPropagation();
            if (confirm('Delete this report?')) {
              del.mutate(r.id, { onSuccess: () => toast.success('Report deleted') });
            }
          }}
          aria-label="Delete report"
        >
          <Trash2 className="size-3.5" strokeWidth={1.75} />
        </Button>
      )}
    </div>
  );
}

function ReportView({ id, onBack }: { id: number; onBack: () => void }) {
  const q = useResearchReport(id);
  const share = useShareResearch();
  const r = q.data;

  // Error before spinner: a 404/403/network failure previously fell through
  // to the isLoading guard forever (isLoading false, data undefined) — an
  // unrecoverable spinner with no way back.
  if (q.isError || (!q.isLoading && !r)) {
    return (
      <div className="py-10 text-sm">
        <p className="mb-3 text-muted-foreground">
          Couldn't load this report — it may have been deleted or unshared.
        </p>
        <Button variant="outline" size="sm" onClick={onBack} className="gap-1.5">
          <ArrowLeft className="size-4" strokeWidth={1.75} /> All research
        </Button>
      </div>
    );
  }
  if (q.isLoading || !r) {
    return (
      <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Loading report…
      </div>
    );
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5 -ml-2">
          <ArrowLeft className="size-4" strokeWidth={1.75} /> All research
        </Button>
        {r.mine && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <Share2 className="size-3.5" strokeWidth={1.75} />
            Share with the team
            <Switch
              checked={r.shared}
              onCheckedChange={(v) =>
                share.mutate(
                  { id, shared: !!v },
                  {
                    onSuccess: () =>
                      toast.success(v ? 'Shared — the team can open this report' : 'Made private'),
                  },
                )
              }
            />
          </label>
        )}
      </div>

      <h1 className="text-2xl font-semibold tracking-tight">
        {r.report?.title ?? r.question}
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">
        “{r.question}”
        {r.stats?.matchingMessages != null && (
          <>
            {' '}
            · {r.stats.matchingMessages} messages analysed ({r.stats.scannedMessages} scanned)
            {r.stats.addresses && r.stats.addresses.length > 0 && (
              <> · addresses: {r.stats.addresses.join(', ')}</>
            )}
          </>
        )}
      </p>

      {(r.status === 'queued' || r.status === 'running') && (
        <div className="mt-6 flex items-center gap-2 rounded-lg border border-violet-500/30 bg-violet-500/5 px-4 py-3 text-sm">
          <Loader2 className="size-4 animate-spin text-violet-500" strokeWidth={2} />
          {r.progress || 'Working…'}
        </div>
      )}
      {r.status === 'failed' && (
        <div className="mt-6 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Research failed: {r.error ?? 'unknown error'} — try rephrasing or run it again.
        </div>
      )}

      <div className="mt-6 space-y-6">
        {(r.report?.sections ?? []).map((s, i) => (
          <SectionView key={i} s={s} />
        ))}
      </div>
    </>
  );
}

function SectionView({ s }: { s: ReportSection }) {
  return (
    <section>
      <h2 className="mb-2 text-lg font-semibold tracking-tight">{s.heading}</h2>
      {s.body &&
        s.body
          .split('\n')
          .filter((p) => p.trim())
          .map((p, i) => (
            <p key={i} className="mb-2 text-sm leading-relaxed">
              {p}
            </p>
          ))}
      {s.table && s.table.columns.length > 0 && (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left">
                {s.table.columns.map((c, i) => (
                  <th key={i} className="px-3 py-2 text-xs font-medium text-muted-foreground">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {s.table.rows.map((row, ri) => (
                <tr key={ri} className="border-b last:border-b-0">
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-3 py-1.5 align-top">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {s.threadIds && s.threadIds.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {s.threadIds.slice(0, 10).map((tid) => (
            <Link
              key={tid}
              to="/inbox"
              search={{ thread: tid }}
              className="rounded-md border bg-card px-2 py-1 text-[11px] hover:bg-accent"
            >
              open thread #{tid}
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
